import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';

const DISPOSABLE_DOMAINS = new Set([
  'mailinator.com', 'guerrillamail.com', 'temp-mail.org', 'throwam.com',
  'fakeinbox.com', 'sharklasers.com', 'guerrillamailblock.com', 'grr.la',
  'guerrillamail.info', 'guerrillamail.biz', 'guerrillamail.de', 'guerrillamail.net',
  'guerrillamail.org', 'spam4.me', 'trashmail.com', 'trashmail.at', 'trashmail.io',
  'trashmail.me', 'trashmail.net', 'yopmail.com', 'yopmail.fr', 'cool.fr.nf',
  'jetable.fr.nf', 'nospam.ze.tc', 'nospam.wins.nl', 'speed.1s.fr', 'courriel.fr.nf',
  'moncourrier.fr.nf', 'monemail.fr.nf', 'monmail.fr.nf', 'dispostable.com',
  'mailnull.com', 'spamgourmet.com', 'spamgourmet.net', 'spamgourmet.org',
  'maildrop.cc', 'filzmail.com', 'spamfree24.org', 'throwam.com', 'throwam.net',
  'tempinbox.co.uk', 'tempinbox.com', 'tempemail.net', 'mailtemporaire.fr',
  'discard.email', 'getonemail.com', 'mytemp.email', 'emailondeck.com',
  'tempail.com', 'spamhereplease.com',
]);

function validate(form) {
  const errors = {};

  const nameClean = form.full_name.trim();
  if (!nameClean) {
    errors.full_name = 'Name is required';
  } else if (nameClean.length < 2) {
    errors.full_name = 'Name must be at least 2 characters';
  } else if (nameClean.length > 50) {
    errors.full_name = 'Name must be under 50 characters';
  } else if (!/^[a-zA-Z\s'\-]+$/.test(nameClean)) {
    errors.full_name = 'Name can only contain letters, spaces, hyphens, or apostrophes';
  } else if (/^(.)\1{4,}$/.test(nameClean.replace(/\s/g, ''))) {
    errors.full_name = 'Please enter a valid name';
  }

  const emailClean = form.email.trim().toLowerCase();
  if (!emailClean) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailClean)) {
    errors.email = 'Enter a valid email address';
  } else {
    const domain = emailClean.split('@')[1];
    if (DISPOSABLE_DOMAINS.has(domain)) {
      errors.email = 'Disposable email addresses are not allowed';
    }
  }

  const phoneClean = form.phone.replace(/\D/g, '');
  if (!phoneClean) {
    errors.phone = 'Phone number is required';
  } else if (phoneClean.length !== 10) {
    errors.phone = 'Enter a valid 10-digit mobile number';
  } else if (!/^[6-9]/.test(phoneClean)) {
    errors.phone = 'Must be a valid Indian mobile number (starts with 6–9)';
  } else if (/^(\d)\1{9}$/.test(phoneClean)) {
    errors.phone = 'Enter a valid mobile number';
  }

  if (!form.password) {
    errors.password = 'Password is required';
  } else if (form.password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  } else if (!/[A-Z]/.test(form.password)) {
    errors.password = 'Password must include at least one uppercase letter';
  } else if (!/[0-9]/.test(form.password)) {
    errors.password = 'Password must include at least one number';
  }

  if (!form.confirm) {
    errors.confirm = 'Please confirm your password';
  } else if (form.password !== form.confirm) {
    errors.confirm = 'Passwords do not match';
  }

  return errors;
}

function LegalModal({ pageType, onClose }) {
  const [page, setPage] = useState(null);
  const cache = useRef({});

  useEffect(() => {
    if (cache.current[pageType]) {
      setPage(cache.current[pageType]);
      return;
    }
    fetch(`/api/legal/${pageType}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(data => { cache.current[pageType] = data; setPage(data); })
      .catch(() => setPage({ title: pageType === 'terms' ? 'Terms & Conditions' : 'Privacy Policy', sections: [], error: true }));
  }, [pageType]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-[#111827] border border-gray-700 rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div>
            <h2 className="text-white font-semibold text-lg">{page?.title ?? '...'}</h2>
            {page?.last_updated && (
              <p className="text-gray-500 text-xs mt-0.5">Last updated: {page.last_updated}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">&times;</button>
        </div>
        <div className="overflow-y-auto px-6 py-4 text-sm text-gray-300 leading-relaxed">
          {!page && (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-3 bg-[#1F2937] rounded w-1/3 mb-2" />
                  <div className="h-3 bg-[#1F2937] rounded w-full mb-1" />
                  <div className="h-3 bg-[#1F2937] rounded w-5/6" />
                </div>
              ))}
            </div>
          )}
          {page?.error && (
            <p className="text-gray-400 text-sm">Unable to load content. Please try again later.</p>
          )}
          {(page?.sections || []).map((s, i) => (
            <div key={i} className="mb-5">
              <h3 className="text-white font-medium mb-1">{s.heading}</h3>
              <p className="whitespace-pre-wrap">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-700 text-right">
          <button onClick={onClose} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function SignupPage() {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', password: '', confirm: '' });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [modal, setModal] = useState(null); // 'terms' | 'privacy' | null
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [otpEmail, setOtpEmail] = useState(null); // set after signup → shows OTP screen
  const { signup } = useAuth();
  const navigate = useNavigate();

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    if (fieldErrors[field]) setFieldErrors(fe => ({ ...fe, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error('Please fix the errors before continuing');
      return;
    }
    if (!agreed) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }
    setLoading(true);
    try {
      const res = await signup(form.full_name.trim(), form.email.trim().toLowerCase(), form.phone.replace(/\D/g, ''), form.password);
      if (res?.status === 'otp_required') {
        setOtpEmail(res.email);
      } else {
        toast.success('Account created! Welcome to TradeReflex.');
        navigate('/');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Signup failed';
      toast.error(typeof msg === 'string' ? msg : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (otpEmail) {
    return <OtpScreen email={otpEmail} onVerified={() => navigate('/')} />;
  }

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-[#1F2937] border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 ${
      fieldErrors[field]
        ? 'border-red-500 focus:ring-red-500'
        : 'border-[#374151] focus:ring-blue-500'
    }`;

  return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-4">
          <div className="flex justify-center mb-1">
            <Logo size="xl" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">TradeReflex</h1>
          <p className="text-gray-400">Create your paper trading account</p>
        </div>

        <div className="bg-[#111827] backdrop-blur-xl border border-[#1F2937] rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white mb-6">Create Account</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
              <input type="text" value={form.full_name} onChange={set('full_name')} required
                className={inputClass('full_name')} placeholder="Your full name" />
              {fieldErrors.full_name && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.full_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <input type="email" value={form.email} onChange={set('email')} required
                className={inputClass('email')} placeholder="you@example.com" />
              {fieldErrors.email && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
              <input type="tel" value={form.phone} onChange={set('phone')} required
                className={inputClass('phone')} placeholder="10-digit mobile number" maxLength={10} />
              {fieldErrors.phone && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} required
                  className={inputClass('password')} placeholder="Min. 8 chars, 1 uppercase, 1 number" />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
              <div className="relative">
                <input type={showConfirm ? 'text' : 'password'} value={form.confirm} onChange={set('confirm')} required
                  className={inputClass('confirm')} placeholder="Repeat password" />
                <button type="button" onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                  {showConfirm ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {fieldErrors.confirm && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.confirm}</p>
              )}
            </div>

            <div className="flex items-start gap-3 pt-1">
              <input
                type="checkbox"
                id="agreed"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-600 bg-[#1F2937] accent-emerald-500 cursor-pointer flex-shrink-0"
              />
              <label htmlFor="agreed" className="text-sm text-gray-400 leading-relaxed cursor-pointer">
                I agree to the{' '}
                <button type="button" onClick={() => setModal('terms')} className="text-emerald-400 hover:text-emerald-300 underline">
                  Terms &amp; Conditions
                </button>{' '}
                and{' '}
                <button type="button" onClick={() => setModal('privacy')} className="text-emerald-400 hover:text-emerald-300 underline">
                  Privacy Policy
                </button>
              </label>
            </div>

            <button type="submit" disabled={loading || !agreed}
              className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50">
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <span className="text-gray-500 text-sm">Already have an account? </span>
            <Link to="/login" className="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
              Sign in
            </Link>
          </div>
        </div>
      </div>
      {modal && (
        <LegalModal pageType={modal} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

function OtpScreen({ email, onVerified }) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { verifyOtp, sendOtp } = useAuth();

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      await verifyOtp(email, otp);
      toast.success('Email verified! Welcome to TradeReflex.');
      onVerified();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid or expired code';
      toast.error(typeof msg === 'string' ? msg : 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await sendOtp(email);
      toast.success('New code sent!');
      setResendCooldown(60);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to resend';
      toast.error(typeof msg === 'string' ? msg : 'Failed to resend');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-4">
          <div className="flex justify-center mb-1">
            <Logo size="xl" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">Check your email</h1>
          <p className="text-gray-400">
            We sent a 6-digit code to<br />
            <span className="text-emerald-400 font-medium">{email}</span>
          </p>
        </div>

        <div className="bg-[#111827] border border-[#1F2937] rounded-2xl p-8">
          <form onSubmit={handleVerify} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-2 py-4 bg-[#1F2937] border border-[#374151] rounded-lg text-white text-center text-xl font-bold tracking-[0.3em] placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="------"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>

          <div className="mt-5 text-center">
            <p className="text-gray-500 text-sm mb-2">Didn't receive the code?</p>
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="text-emerald-400 hover:text-emerald-300 text-sm font-medium disabled:text-gray-600 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
            </button>
            <p className="text-gray-400 text-sm mt-3 font-medium">
              Can't find it? Check your <span className="text-emerald-400 font-semibold">spam or junk</span> folder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
